import { useState, useEffect } from "react";
import { Shield, Key, UserCheck, AlertCircle } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

type SecurityLevel = "basic" | "protected" | "verified";

interface SecuritySettingsProps {
  defaultLevel?: SecurityLevel;
  defaultPasscode?: string;
  onSecurityChange?: (level: SecurityLevel, passcode?: string) => void;
}

export default function SecuritySettings({
  defaultLevel = "basic",
  defaultPasscode = "",
  onSecurityChange = () => {},
}: SecuritySettingsProps) {
  const [securityLevel, setSecurityLevel] = useState<SecurityLevel>(defaultLevel);
  const [passcode, setPasscode] = useState(defaultPasscode);
  const [error, setError] = useState("");

  useEffect(() => {
    if (securityLevel !== "protected") {
      setPasscode("");
      setError("");
    }
  }, [securityLevel]);

  const handleSecurityChange = (level: SecurityLevel) => {
    setSecurityLevel(level);
    if (level !== "protected") {
      onSecurityChange(level, undefined);
    } else {
      // When Protected is selected, immediately call parent with empty passcode
      onSecurityChange("protected", "");
      setPasscode("");
      setError("");
    }
  };

  const handlePasscodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPasscode = e.target.value;
    setPasscode(newPasscode);
    
    if (error) {
      setError("");
    }
    
    // Call parent callback immediately on every change
    if (securityLevel === "protected") {
      onSecurityChange("protected", newPasscode);
    }
  };

  const handlePasscodeBlur = () => {
    if (securityLevel === "protected" && passcode.trim()) {
      onSecurityChange("protected", passcode);
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex items-baseline gap-2">
        <h3 className="text-sm font-semibold">Security Level</h3>
        <p className="text-xs text-muted-foreground">— Choose how others can access your cipher</p>
      </div>

      <RadioGroup
        value={securityLevel}
        onValueChange={(value) =>
          handleSecurityChange(value as SecurityLevel)
        }
        className="space-y-0.5"
      >
        <div
          className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg border-2 transition-colors cursor-pointer ${
            securityLevel === "basic"
              ? "border-purple-500 bg-purple-50"
              : "border-gray-200 hover:bg-gray-50"
          }`}
          onClick={() => handleSecurityChange("basic")}
        >
          <RadioGroupItem value="basic" id="basic" className="shrink-0" />
          <Shield className="h-6 w-6 shrink-0 text-[hsl(var(--neon-green))]" />
          <div className="min-w-0">
            <Label htmlFor="basic" className="text-sm font-medium cursor-pointer">Basic</Label>
            <p className="text-xs text-gray-500">Open — anyone with the cipher code can decrypt.</p>
          </div>
        </div>

        <div
          className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg border-2 transition-colors cursor-pointer ${
            securityLevel === "protected"
              ? "border-purple-500 bg-purple-50"
              : "border-gray-200 hover:bg-gray-50"
          }`}
          onClick={() => handleSecurityChange("protected")}
        >
          <RadioGroupItem value="protected" id="protected" className="shrink-0" />
          <Key className="h-6 w-6 shrink-0 text-[hsl(var(--neon-blue))]" />
          <div className="flex-1 min-w-0">
            <Label htmlFor="protected" className="text-sm font-medium cursor-pointer">Protected</Label>
            <p className="text-xs text-gray-500">Requires a 4-digit passcode to access.</p>

            {securityLevel === "protected" && (
              <div className="mt-1.5 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <Input
                  id="passcode"
                  type="password"
                  placeholder="4-digit passcode"
                  maxLength={4}
                  className={`flex-1 h-7 text-sm ${error ? 'border-red-500' : !passcode.trim() ? 'border-yellow-500 focus:ring-yellow-500' : passcode.length < 4 ? 'border-orange-500' : 'border-green-500'}`}
                  value={passcode}
                  onChange={handlePasscodeChange}
                  onBlur={handlePasscodeBlur}
                  required
                />
                {passcode.length === 4 && (
                  <span className="text-xs text-green-600 whitespace-nowrap">Set ✓</span>
                )}
                {passcode.trim() && passcode.length > 0 && passcode.length < 4 && (
                  <span className="text-xs text-orange-600 whitespace-nowrap">{passcode.length}/4</span>
                )}
              </div>
            )}
            {securityLevel === "protected" && error && (
              <p className="text-xs text-red-500 mt-1">{error}</p>
            )}
          </div>
        </div>

        <div
          className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg border-2 transition-colors cursor-pointer ${
            securityLevel === "verified"
              ? "border-purple-500 bg-purple-50"
              : "border-gray-200 hover:bg-gray-50"
          }`}
          onClick={() => handleSecurityChange("verified")}
        >
          <RadioGroupItem value="verified" id="verified" className="shrink-0" />
          <UserCheck className="h-6 w-6 shrink-0 text-primary" />
          <div className="min-w-0">
            <Label htmlFor="verified" className="text-sm font-medium cursor-pointer">Verified</Label>
            <p className="text-xs text-gray-500">You approve each user's access request.</p>
          </div>
        </div>
      </RadioGroup>
    </div>
  );
}
